import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { MapPin, Phone, Mail, Clock, Navigation, PhoneCall, ArrowDown, X } from 'lucide-react';
import { mockData } from '../data/mock';

// Images de fond par nom d'agence
const agencyBackgrounds = {
  'Châteaurenard': '/assets/photos/agences/chateaurenard.webp',
  'Arles': '/assets/photos/agences/arles.webp',
  'Caissargues': '/assets/photos/agences/caissargues00.webp',
  'Fos-sur-Mer': '/assets/photos/agences/caissargues01.webp',
};

// Liens Google Maps
const agencyMapsLinks = {
  'Fos-sur-Mer': 'https://maps.app.goo.gl/GejvBc6KbBSwvUGx6',
  'Arles': 'https://maps.app.goo.gl/BquWGSqdXBpb68ke6',
  'Châteaurenard': 'https://maps.app.goo.gl/uk2PXywRT7R8pYPU9',
  'Caissargues': 'https://maps.app.goo.gl/7ps1s3BWAR4ytD5s8',
};

// Modal rendu via Portal directement dans <body> pour éviter tout conflit DOM
const AgencyModal = ({ agency, onClose }) => {
  // Échap ferme le modal. Déclaré avant le retour anticipé (règles des hooks).
  useEffect(() => {
    if (!agency) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [agency, onClose]);

  if (!agency) return null;

  const mapsLink = agency.mapsLink || agencyMapsLinks[agency.name];
  const lat = agency.lat ?? (agency.coordinates?.lat);
  const lng = agency.lng ?? (agency.coordinates?.lng);
  const fallbackMaps = lat && lng
    ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${agency.address}, ${agency.zipCode} ${agency.city}`)}`;
  const bgImage = agencyBackgrounds[agency.name];

  const modalContent = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="agency-modal-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        overflowY: 'auto',
      }}
    >
      {/* Overlay — ferme le modal au clic. Redondant avec le bouton X et
          Échap, donc masqué aux technologies d'assistance. */}
      <div
        aria-hidden="true"
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.65)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          cursor: 'pointer',
        }}
      />

      {/* Carte modale — frère de l'overlay, donc aucun clic à intercepter */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: '28rem',
          background: '#1a2e28',
          borderRadius: '1.5rem',
          boxShadow: '0 25px 50px rgba(0,0,0,0.5)',
          overflow: 'hidden',
          margin: 'auto',
        }}
      >
        {/* Bouton fermeture — en dehors de la zone image pour éviter tout conflit */}
        <div style={{ position: 'relative' }}>
          {/* Zone image header */}
          <div
            style={{
              position: 'relative',
              padding: '24px 24px 16px',
              textAlign: 'center',
              backgroundImage: bgImage ? `url(${bgImage})` : 'none',
              backgroundColor: bgImage ? undefined : '#2A4A42',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              minHeight: '140px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
            }}
          >
            {bgImage && (
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(to top, #1a2e28 0%, rgba(30,58,51,0.85) 50%, rgba(30,58,51,0.5) 100%)',
              }} />
            )}
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#70AD25',
                borderRadius: '50%',
                width: '48px',
                height: '48px',
                marginBottom: '12px',
              }}>
                <MapPin size={22} color="#1E3A33" />
              </div>
              <h3 id="agency-modal-title" style={{ fontSize: '1.5rem', fontWeight: 900, color: 'white', textTransform: 'uppercase', margin: '0 0 4px' }}>
                {agency.name}
              </h3>
              <p style={{ color: '#8BC34A', fontSize: '0.875rem', margin: '0 0 2px' }}>
                {agency.address}, {agency.zipCode} {agency.city}
              </p>
              <p style={{ color: 'rgba(139,195,74,0.7)', fontSize: '0.75rem', margin: 0 }}>
                Lun-Ven : {agency.hours.weekdays}
              </p>
            </div>
          </div>

          {/* Bouton X — placé en overlay absolu sur toute la carte, z-index très élevé */}
          <button
            onClick={onClose}
            aria-label="Fermer"
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              zIndex: 100,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.15)',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Actions */}
        <div style={{ padding: '16px 20px 20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <a
            href={`tel:${agency.phone}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              background: '#70AD25',
              borderRadius: '1rem',
              padding: '14px 18px',
              textDecoration: 'none',
              color: '#1E3A33',
              fontWeight: 700,
              fontSize: '1rem',
              transition: 'opacity 0.15s ease',
            }}
          >
            <div style={{ background: 'rgba(30,58,51,0.2)', borderRadius: '50%', padding: '8px', display: 'flex' }}>
              <Phone size={20} />
            </div>
            <div>
              <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', opacity: 0.7, marginBottom: '2px' }}>Appeler</div>
              <div>{agency.phone}</div>
            </div>
          </a>

          <a
            href={mapsLink || fallbackMaps}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              background: '#2A4A42',
              borderRadius: '1rem',
              padding: '14px 18px',
              textDecoration: 'none',
              color: 'white',
              fontWeight: 700,
              fontSize: '1rem',
              border: '1px solid rgba(112,173,37,0.3)',
              transition: 'opacity 0.15s ease',
            }}
          >
            <div style={{ background: 'rgba(112,173,37,0.15)', borderRadius: '50%', padding: '8px', display: 'flex' }}>
              <Navigation size={20} color="#70AD25" />
            </div>
            <div>
              <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#8BC34A', marginBottom: '2px' }}>Itinéraire</div>
              <div style={{ fontSize: '0.875rem', color: '#8BC34A' }}>Ouvrir dans Maps</div>
            </div>
          </a>

          <a
            href={`mailto:${agency.email}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              background: '#2A4A42',
              borderRadius: '1rem',
              padding: '14px 18px',
              textDecoration: 'none',
              color: 'white',
              fontWeight: 700,
              fontSize: '1rem',
              border: '1px solid rgba(112,173,37,0.3)',
              transition: 'opacity 0.15s ease',
            }}
          >
            <div style={{ background: 'rgba(112,173,37,0.15)', borderRadius: '50%', padding: '8px', display: 'flex' }}>
              <Mail size={20} color="#70AD25" />
            </div>
            <div style={{ overflow: 'hidden' }}>
              <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#8BC34A', marginBottom: '2px' }}>Email</div>
              <div style={{ fontSize: '0.875rem', color: '#8BC34A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{agency.email}</div>
            </div>
          </a>
        </div>
      </div>
    </div>
  );

  // Portal = rendu directement dans <body>, hors de tout DOM imbriqué
  return ReactDOM.createPortal(modalContent, document.body);
};

const Agencies = () => {
  const [selectedAgency, setSelectedAgency] = useState(null);

  return (
    <section id="agences" className="py-24 bg-[#1E3A33]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12" data-aos="fade-up">
          <h2 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white uppercase tracking-tight mb-4">
            Nos agences
          </h2>
          <p className="text-xl text-[#8BC34A] max-w-3xl mx-auto">
            4 agences dans le sud de la France pour vous accompagner au plus proche de votre projet
          </p>
        </div>

        {/* Bloc numéro unique — tout le bloc est cliquable */}
        <div className="relative mb-16" data-aos="zoom-in" data-aos-delay="200">
          <a
            href="tel:0490471578"
            className="block bg-gradient-to-br from-[#70AD25] to-[#8BC34A] rounded-3xl p-8 md:p-12 text-center max-w-4xl mx-auto shadow-2xl hover:shadow-[#70AD25]/40 hover:scale-[1.02] transition-all duration-300 cursor-pointer"
            style={{ textDecoration: 'none' }}
          >
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="bg-[#1E3A33] p-4 rounded-full">
                <PhoneCall className="w-8 h-8 md:w-10 md:h-10 text-[#70AD25]" />
              </div>
              <span className="text-[#1E3A33] font-bold text-lg md:text-xl uppercase tracking-wider">
                Un seul numéro
              </span>
            </div>

            <div className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-[#1E3A33] mb-6">
              04 90 47 15 78
            </div>

            <div className="bg-[#1E3A33]/20 rounded-2xl p-4 md:p-6 backdrop-blur-sm">
              <p className="text-[#1E3A33] font-bold text-lg md:text-xl mb-2">
                🏢 4 agences — 1 numéro à appeler
              </p>
              <p className="text-[#1E3A33]/80 text-sm md:text-base max-w-2xl mx-auto">
                Notre secrétariat centralisé vous accueille et vous redirige vers l'agence la plus proche de votre projet. Simple, rapide, efficace !
              </p>
            </div>
          </a>

          {/* Flèches desktop */}
          <div className="hidden xl:flex justify-center mt-8 gap-4">
            {[0, 100, 200, 300].map((delay) => (
              <div key={delay} className="flex flex-col items-center text-[#70AD25] animate-bounce" style={{ animationDelay: `${delay}ms` }}>
                <ArrowDown className="w-8 h-8" />
              </div>
            ))}
          </div>

          {/* Flèche mobile */}
          <div className="xl:hidden flex justify-center mt-6">
            <div className="flex flex-col items-center text-[#70AD25] animate-bounce">
              <ArrowDown className="w-10 h-10" />
              <span className="text-sm font-bold mt-1">Nos 4 agences</span>
            </div>
          </div>
        </div>

        {/* Grille des agences */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
          {mockData.agencies.map((agency, index) => {
            const bgImage = agencyBackgrounds[agency.name];
            return (
              <button
                key={agency.id}
                type="button"
                onClick={() => setSelectedAgency(agency)}
                className={`rounded-2xl p-6 transition-all duration-300 flex flex-col text-left hover:-translate-y-2 hover:shadow-xl hover:shadow-[#70AD25]/20 relative overflow-hidden w-full cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#70AD25] ${bgImage ? 'shadow-lg' : 'bg-[#2A4A42] border-2 border-[#3A5A4A]/40 hover:border-[#70AD25]'}`}
                style={bgImage ? {
                  backgroundImage: `url(${bgImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                } : {}}
                aria-label={`Contacter l'agence de ${agency.name}`}
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                {bgImage && (
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A33]/95 via-[#1E3A33]/80 to-[#1E3A33]/60 z-0" />
                )}

                <div className="absolute top-0 right-0 bg-[#70AD25] text-[#1E3A33] text-xs font-bold px-3 py-1 rounded-bl-xl z-10">
                  Agence {index + 1}
                </div>

                <div className="relative z-10 flex flex-col flex-1">
                  <h3 className="text-2xl font-black text-white uppercase mb-6 text-center mt-2">
                    {agency.name}
                  </h3>

                  <div className="space-y-4 flex-1">
                    <div className="flex items-start gap-3">
                      <div className="bg-[#70AD25] p-2 rounded-full flex-shrink-0">
                        <MapPin className="w-4 h-4 text-[#1E3A33]" />
                      </div>
                      <p className="text-[#8BC34A] text-sm">
                        {agency.address}<br />
                        {agency.zipCode} {agency.city}
                      </p>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="bg-[#70AD25] p-2 rounded-full flex-shrink-0">
                        <Phone className="w-4 h-4 text-[#1E3A33]" />
                      </div>
                      <span className="text-[#8BC34A] text-sm">{agency.phone}</span>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="bg-[#70AD25] p-2 rounded-full flex-shrink-0">
                        <Clock className="w-4 h-4 text-[#1E3A33]" />
                      </div>
                      <div className="text-[#8BC34A] text-sm">
                        <p>Lun-Ven: {agency.hours.weekdays}</p>
                        <p>Sam-Dim: {agency.hours.saturday}</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex items-center justify-center gap-2 text-[#8BC34A] text-sm font-bold">
                    <PhoneCall className="w-4 h-4" />
                    Appuyer pour contacter
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Portal modal — rendu dans <body> */}
      <AgencyModal
        agency={selectedAgency}
        onClose={() => setSelectedAgency(null)}
      />
    </section>
  );
};

export default Agencies;
